import { Link } from '@tanstack/react-router';
import { Plus, RefreshCw } from 'lucide-react';

import { DataTable } from '@/components/data-table/data-table';
import { Button } from '@/components/ui/button';
import { useAdminUsers, useSyncIntercomTeammates } from '@/lib/api/users';
import {
  usersColumns,
  usersIntercomTeammateFacet,
  usersStatusFacet,
} from '@/routes/users/users-columns';

export function UsersListPage() {
  const usersQuery = useAdminUsers();
  const syncTeammates = useSyncIntercomTeammates();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Admins</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Admins who can sign in to the control plane. An admin is matched to their Intercom
            teammate by email; without one, they cannot reply to Intercom conversations.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={syncTeammates.isPending}
            onClick={() => syncTeammates.mutate()}
          >
            <RefreshCw className="size-4" />
            Refresh from Intercom
          </Button>
          <Button asChild>
            <Link to="/users/new">
              <Plus className="size-4" />
              Add admin
            </Link>
          </Button>
        </div>
      </div>

      {syncTeammates.isSuccess && (
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Synced {syncTeammates.data.teammates} Intercom teammates.
        </p>
      )}
      {syncTeammates.isError && (
        <p className="text-sm text-[var(--color-destructive)]">
          Failed to refresh from Intercom: {syncTeammates.error.message}
        </p>
      )}

      {usersQuery.isPending && <p className="text-sm">Loading…</p>}
      {usersQuery.isError && (
        <p className="text-sm text-[var(--color-destructive)]">
          Failed to load users: {usersQuery.error.message}
        </p>
      )}
      {usersQuery.data && (
        <DataTable
          columns={usersColumns}
          data={usersQuery.data}
          filterColumn={{ id: 'email', placeholder: 'Filter by email…' }}
          facets={[usersStatusFacet, usersIntercomTeammateFacet]}
          emptyMessage="No admins yet."
        />
      )}
    </div>
  );
}
